import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Lock, Unlock, X, Eye, EyeOff } from 'lucide-react';

const CHILD_LOCK_CODE = '8648';
const CHILD_LOCK_STORAGE_KEY = 'hal3000_child_lock';

// Context for child lock state
const ChildLockContext = createContext(null);

// Callback pour déclencher la vidéo HAL (sera défini par App.js)
let onLockedActionAttempt = null;
export const setLockedActionCallback = (callback) => {
  onLockedActionAttempt = callback;
};

export const useChildLock = () => {
  const context = useContext(ChildLockContext);
  if (!context) {
    throw new Error('useChildLock must be used within ChildLockProvider');
  }
  return context;
};

export const ChildLockProvider = ({ children }) => {
  const [isLocked, setIsLocked] = useState(() => {
    const saved = localStorage.getItem(CHILD_LOCK_STORAGE_KEY);
    return saved === 'true';
  });
  const lockedAttemptCountRef = useRef(0);

  useEffect(() => {
    localStorage.setItem(CHILD_LOCK_STORAGE_KEY, isLocked.toString());
  }, [isLocked]);

  const lock = useCallback(() => {
    setIsLocked(true);
  }, []);

  const unlock = useCallback((code) => {
    if (code === CHILD_LOCK_CODE) {
      setIsLocked(false);
      lockedAttemptCountRef.current = 0;
      return true;
    }
    // Mauvais code = tentative d'action non autorisée
    lockedAttemptCountRef.current += 1;
    if (lockedAttemptCountRef.current >= 3 && onLockedActionAttempt) {
      onLockedActionAttempt();
      lockedAttemptCountRef.current = 0;
    }
    return false;
  }, []);

  // Fonction appelée quand une action est bloquée
  const onBlockedAction = useCallback(() => {
    lockedAttemptCountRef.current += 1;
    if (lockedAttemptCountRef.current >= 2 && onLockedActionAttempt) {
      onLockedActionAttempt();
      lockedAttemptCountRef.current = 0;
    }
  }, []);

  return (
    <ChildLockContext.Provider value={{ isLocked, lock, unlock, onBlockedAction }}>
      {children}
    </ChildLockContext.Provider>
  );
};

// Lock toggle button for sidebar/header
export const ChildLockToggle = ({ compact = false }) => {
  const { isLocked, lock, unlock } = useChildLock();
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const handleToggle = () => {
    if (isLocked) {
      setShowUnlockModal(true);
      setCode('');
      setError(false);
    } else {
      lock();
    }
  };

  const handleUnlock = () => {
    if (unlock(code)) {
      setShowUnlockModal(false);
      setCode('');
    } else {
      setError(true);
      setCode('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUnlock();
    }
  };

  if (compact) {
    return (
      <>
        <button
          onClick={handleToggle}
          className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-mono transition-all ${
            isLocked 
              ? 'bg-red-900/50 border border-red-500 text-red-400' 
              : 'bg-green-900/50 border border-green-500 text-green-400'
          }`}
          title={isLocked ? 'Mode enfant actif - Cliquez pour déverrouiller' : 'Cliquez pour verrouiller'}
          data-testid="child-lock-toggle"
        >
          {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
          <span>{isLocked ? 'VERROUILLÉ' : 'LIBRE'}</span>
        </button>

        {/* Unlock Modal */}
        {showUnlockModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]" onClick={() => setShowUnlockModal(false)}>
            <div 
              className="bg-[#0a0e27] border-2 border-cyan-400 rounded-lg p-6 w-80 shadow-2xl"
              style={{ boxShadow: '0 0 30px rgba(0, 240, 255, 0.5)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-cyan-400 font-['Orbitron']">DÉVERROUILLAGE</h3>
                <button onClick={() => setShowUnlockModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-gray-400 mb-4 font-mono">
                Entrez le code parental pour déverrouiller les contrôles.
              </p>
              
              <div className="relative mb-4">
                <input
                  type={showCode ? 'text' : 'password'}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 4));
                    setError(false);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="****"
                  className={`w-full px-4 py-3 bg-black border-2 rounded text-center text-2xl font-mono tracking-widest ${
                    error ? 'border-red-500 text-red-400' : 'border-cyan-400 text-cyan-400'
                  }`}
                  autoFocus
                  data-testid="child-lock-code-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCode(!showCode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-cyan-400"
                >
                  {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {error && (
                <p className="text-red-400 text-xs font-mono mb-4 text-center animate-pulse">
                  CODE INCORRECT - ACCÈS REFUSÉ
                </p>
              )}
              
              <button
                onClick={handleUnlock}
                disabled={code.length !== 4}
                className="w-full py-3 bg-cyan-400 text-black font-bold rounded hover:bg-cyan-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                data-testid="child-lock-submit"
              >
                DÉVERROUILLER
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Full version for sidebar
  return (
    <>
      <div className="p-2 border-b border-[#00ff9f] border-opacity-30">
        <button
          onClick={handleToggle}
          className={`w-full flex items-center justify-between px-3 py-2 rounded text-xs font-mono transition-all ${
            isLocked 
              ? 'bg-red-900/30 border border-red-500/50 text-red-400' 
              : 'bg-green-900/30 border border-green-500/50 text-green-400'
          }`}
          data-testid="child-lock-toggle-full"
        >
          <span className="flex items-center space-x-2">
            {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            <span>MODE ENFANT</span>
          </span>
          <span className={`px-2 py-0.5 rounded text-[10px] ${
            isLocked ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
            {isLocked ? 'ACTIF' : 'OFF'}
          </span>
        </button>
      </div>

      {/* Unlock Modal - same as compact version */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]" onClick={() => setShowUnlockModal(false)}>
          <div 
            className="bg-[#0a0e27] border-2 border-cyan-400 rounded-lg p-6 w-80 shadow-2xl"
            style={{ boxShadow: '0 0 30px rgba(0, 240, 255, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-cyan-400 font-['Orbitron']">DÉVERROUILLAGE</h3>
              <button onClick={() => setShowUnlockModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-400 mb-4 font-mono">
              Entrez le code parental pour déverrouiller les contrôles.
            </p>
            
            <div className="relative mb-4">
              <input
                type={showCode ? 'text' : 'password'}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 4));
                  setError(false);
                }}
                onKeyPress={handleKeyPress}
                placeholder="****"
                className={`w-full px-4 py-3 bg-black border-2 rounded text-center text-2xl font-mono tracking-widest ${
                  error ? 'border-red-500 text-red-400' : 'border-cyan-400 text-cyan-400'
                }`}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowCode(!showCode)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-cyan-400"
              >
                {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-400 text-xs font-mono mb-4 text-center animate-pulse">
                CODE INCORRECT - ACCÈS REFUSÉ
              </p>
            )}
            
            <button
              onClick={handleUnlock}
              disabled={code.length !== 4}
              className="w-full py-3 bg-cyan-400 text-black font-bold rounded hover:bg-cyan-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-mono"
            >
              DÉVERROUILLER
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// HOC to wrap action buttons with child lock protection
export const ProtectedButton = ({ children, onClick, className, ...props }) => {
  const { isLocked } = useChildLock();

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      // Could show a toast here
      return;
    }
    onClick?.(e);
  };

  return (
    <button
      {...props}
      onClick={handleClick}
      className={`${className} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={isLocked || props.disabled}
      title={isLocked ? 'Mode enfant actif - Contrôle désactivé' : props.title}
    >
      {children}
      {isLocked && (
        <Lock className="w-3 h-3 ml-1 inline-block" />
      )}
    </button>
  );
};

export default { ChildLockProvider, useChildLock, ChildLockToggle, ProtectedButton };
